import { z } from 'zod';

import { zodTimeStamp } from '@/utils/zodUtils';

/**
 * Location layout API schema. Mirrors the AGREED contract in parking-app-api:
 * per-location, per-level lot layouts made of equipment markers. Field names are
 * kept in the API's snake_case so the wire shape and the editor's in-memory model
 * are the same object (no mapping layer).
 *
 * A marker is positioned in exactly one of two ways:
 * - image-based: `x`/`y` normalized to [0,1] relative to the level's uploaded
 *   base image (the original v1 editor);
 * - map-based: `position`, a geographic point (`{lat, lng}`) the marker's icon
 *   is dropped on.
 *
 * `rotation` (degrees clockwise, 0 = up/north) applies to both.
 */

export const MARKER_ID_MAX_LENGTH = 128;
export const MARKER_TYPE_MAX_LENGTH = 64;
export const MARKER_LABEL_MAX_LENGTH = 200;
export const MAX_MARKERS_PER_LAYOUT = 500;
export const LEVEL_LABEL_MAX_LENGTH = 60;
export const DEFAULT_LEVEL_LABEL = 'Main';

export const GeoPointSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
});

export type GeoPoint = z.infer<typeof GeoPointSchema>;

/**
 * `type` is a pass-through string, NOT an enum: the backend never rejects an
 * unknown type so the frontend marker-type registry can grow without an API
 * change. Rendering falls back to a generic glyph for unrecognised types.
 *
 * `.passthrough()` keeps any unknown fields a newer client or the API adds, so
 * an older client that loads, edits and saves a layout never strips data it
 * does not understand (see `toMarkerPayload`).
 */
const LayoutMarkerObjectSchema = z
  .object({
    id: z.string().min(1).max(MARKER_ID_MAX_LENGTH),
    type: z.string().min(1).max(MARKER_TYPE_MAX_LENGTH),
    x: z.number().min(0).max(1).optional(),
    y: z.number().min(0).max(1).optional(),
    position: GeoPointSchema.nullish(),
    rotation: z.number().min(0).max(360),
    label: z.string().max(MARKER_LABEL_MAX_LENGTH).nullish(),
    pin_protected: z.boolean().nullish(),
  })
  .passthrough();

export const LayoutMarkerSchema = LayoutMarkerObjectSchema.superRefine(
  (marker, ctx) => {
    const hasX = marker.x !== undefined;
    const hasY = marker.y !== undefined;
    if (hasX !== hasY) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'A marker needs both x and y.',
        path: [hasX ? 'y' : 'x'],
      });
      return;
    }
    const hasImagePlacement = hasX && hasY;
    const hasMapPlacement = marker.position != null;
    if (hasImagePlacement === hasMapPlacement) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'A marker is placed either by x/y or by a position, not both or neither.',
        path: ['position'],
      });
    }
  }
);

export type LayoutMarker = z.infer<typeof LayoutMarkerSchema>;

export type ImagePlacedMarker = LayoutMarker & { x: number; y: number };
export type MapPlacedMarker = LayoutMarker & { position: GeoPoint };

export function isImagePlacedMarker(
  marker: LayoutMarker
): marker is ImagePlacedMarker {
  return typeof marker.x === 'number' && typeof marker.y === 'number';
}

export function isMapPlacedMarker(
  marker: LayoutMarker
): marker is MapPlacedMarker {
  return marker.position != null;
}

// `image_url` and `updated_by` are `omitempty` on the API, so a level without an
// image or a writer has no key at all rather than null.
export const LocationLayoutSchema = z.object({
  id: z.string().uuid(),
  location_id: z.string(),
  level_label: z.string(),
  image_url: z.string().nullish(),
  has_image: z.boolean(),
  markers: z.array(LayoutMarkerSchema).max(MAX_MARKERS_PER_LAYOUT),
  updated_by: z.string().nullish(),
  created_at: zodTimeStamp,
  updated_at: zodTimeStamp,
});

export type LocationLayout = z.infer<typeof LocationLayoutSchema>;

export const LocationLayoutListSchema = z.array(LocationLayoutSchema);

export type LocationLayoutList = z.infer<typeof LocationLayoutListSchema>;

/**
 * PUT body. A full replace of a level's marker set: whatever is sent becomes the
 * level's complete markers. The base image is not affected by this call.
 */
export type UpdateLocationLayoutPayload = {
  markers: LayoutMarker[];
};
